import * as Tone from 'tone';

// Audio player manager (restores complete audio playback functionality)
export class AudioPlayerManager {
    constructor() {
        this.players = new Map();
        this.masterGain = null;
        this.outputResolver = null; // optional: function(trackId) => AudioNode
        this.isGlobalPlaying = false;
        this.globalStartTime = 0;
        this.globalPlayheadTime = 0;
        this.activePlayersCount = 0;
        this.playbackEndCallback = null;
    }

    initialize(masterGain, outputResolver = null) {
        this.masterGain = masterGain;
        this.outputResolver = outputResolver;
        console.log('AudioPlayerManager initialized');
    }

    // Set playback end callback
    setPlaybackEndCallback(callback) {
        this.playbackEndCallback = callback;
    }

    createPlayer(clipId, audioBuffer, trackVolume = 1, trackPan = 0, trackId = null) {
        if (this.players.has(clipId)) {
            this.disposePlayer(clipId);
        }

        const player = new Tone.Player(audioBuffer);
        const gainNode = new Tone.Gain(trackVolume);
        const panNode = new Tone.Panner(trackPan);

        player.connect(gainNode);
        gainNode.connect(panNode);
        const destination = (this.outputResolver && trackId != null)
            ? this.outputResolver(trackId)
            : this.masterGain;
        panNode.connect(destination);

        const playerData = {
            player,
            gainNode,
            panNode,
            isPlaying: false,
            audioBuffer,
            trackVolume,
            trackPan,
            trackId
        };

        this.players.set(clipId, playerData);
        console.log(`Player created for clip ${clipId}`);
        return playerData;
    }

    getPlayer(clipId) {
        return this.players.get(clipId);
    }

    updatePlayerVolume(clipId, volume) {
        const playerData = this.players.get(clipId);
        if (playerData) {
            playerData.gainNode.gain.rampTo(Math.max(0, volume), 0.1);
            playerData.trackVolume = volume;
        }
    }

    updatePlayerPan(clipId, pan) {
        const playerData = this.players.get(clipId);
        if (playerData) {
            playerData.panNode.pan.rampTo(Math.max(-1, Math.min(1, pan)), 0.1);
            playerData.trackPan = pan;
        }
    }

    // Convert beats to seconds
    beatsToSeconds(beats, bpm) {
        return (beats * 60) / bpm;
    }

    // Start playing all audio from specified time
    startGlobalPlayback(clips, startTimeInBeats, tracks, bpm) {
        console.log(`=== Starting audio playback from ${startTimeInBeats} beats at ${bpm} BPM ===`);

        // Stop current playback
        this.stopGlobalPlayback();

        this.isGlobalPlaying = true;
        this.globalStartTime = Tone.now();
        this.globalPlayheadTime = startTimeInBeats;
        this.activePlayersCount = 0;

        const startTimeInSeconds = this.beatsToSeconds(startTimeInBeats, bpm);

        // Collect all audio clips that need to be played
        const audioClips = clips.filter(clip => clip.type === 'audio' && clip.audioBuffer);

        console.log(`Found ${audioClips.length} audio clips to play`);

        // Set up playback for each audio clip
        audioClips.forEach((clip, index) => {
            const track = tracks.find(t => t.id === clip.trackId);
            if (!track || track.mute) {
                console.log(`Skipping audio clip ${clip.name} - track muted or not found`);
                return;
            }

            // Ensure player exists
            const playerData = this.getPlayer(clip.id) || this.createPlayer(clip.id, clip.audioBuffer, track.volume, track.pan);
            if (!playerData) {
                console.error(`Failed to create player for audio clip ${clip.id}`);
                return;
            }

            // Calculate clip time range (seconds)
            const clipStartInSeconds = this.beatsToSeconds(clip.start, bpm);
            const clipDurationInSeconds = clip.audioBuffer.duration;
            const clipEndInSeconds = clipStartInSeconds + clipDurationInSeconds;

            console.log(`Audio clip ${index + 1}: "${clip.name}"`);
            console.log(`  - Start: ${clipStartInSeconds.toFixed(2)}s (${clip.start} beats)`);
            console.log(`  - End: ${clipEndInSeconds.toFixed(2)}s`);
            console.log(`  - Playhead: ${startTimeInSeconds.toFixed(2)}s`);

            // Check if clip needs to be played
            if (clipEndInSeconds > startTimeInSeconds) {
                if (clipStartInSeconds >= startTimeInSeconds) {
                    // Clip is after playhead, delay playback
                    const delayInSeconds = clipStartInSeconds - startTimeInSeconds;
                    console.log(`  -> Scheduling audio with delay: ${delayInSeconds.toFixed(2)}s`);

                    this.schedulePlayerStart(playerData, this.globalStartTime + delayInSeconds, 0);
                } else {
                    // Clip has already started, play from middle
                    const offsetInSeconds = startTimeInSeconds - clipStartInSeconds;
                    if (offsetInSeconds < clipDurationInSeconds) {
                        console.log(`  -> Starting audio with offset: ${offsetInSeconds.toFixed(2)}s`);
                        this.schedulePlayerStart(playerData, this.globalStartTime, offsetInSeconds);
                    } else {
                        console.log(`  -> Skipping audio (offset beyond duration)`);
                    }
                }
            } else {
                console.log(`  -> Skipping audio (already ended)`);
            }
        });

        console.log('=== Audio playback setup complete ===');
    }

    // Schedule player to start playback
    schedulePlayerStart(playerData, startTime, offset = 0) {
        try {
            if (playerData.isPlaying) {
                playerData.player.stop();
                this.activePlayersCount--;
            }

            playerData.player.start(startTime, offset);
            playerData.isPlaying = true;
            this.activePlayersCount++;

            // Set end callback
            playerData.player.onended = () => {
                console.log('Audio player ended');
                playerData.isPlaying = false;
                this.activePlayersCount--;

                // Check if all players have ended
                if (this.activePlayersCount <= 0 && this.isGlobalPlaying) {
                    console.log('All audio players ended');
                    this.isGlobalPlaying = false;

                    // Notify main component that playback has ended
                    if (this.playbackEndCallback) {
                        this.playbackEndCallback();
                    }
                }
            };

            console.log(`Audio player scheduled: start=${startTime.toFixed(2)}, offset=${offset.toFixed(2)}`);
        } catch (error) {
            console.error('Failed to start audio player:', error);
        }
    }

    // Stop global playback
    stopGlobalPlayback() {
        console.log('Stopping audio playback');
        this.players.forEach((playerData, clipId) => {
            if (playerData.isPlaying) {
                try {
                    playerData.player.stop();
                    playerData.isPlaying = false;
                } catch (error) {
                    console.warn(`Failed to stop audio player ${clipId}:`, error);
                }
            }
        });
        this.isGlobalPlaying = false;
        this.globalPlayheadTime = 0;
        this.activePlayersCount = 0;
    }

    // Get current playback time
    getCurrentPlaybackTime(bpm) {
        if (!this.isGlobalPlaying) return this.globalPlayheadTime;

        const elapsedSeconds = Tone.now() - this.globalStartTime;
        const elapsedBeats = (elapsedSeconds * bpm) / 60;
        return this.globalPlayheadTime + elapsedBeats;
    }

    // Check if any player is active
    isAnyPlayerActive() {
        return this.activePlayersCount > 0;
    }

    disposePlayer(clipId) {
        const playerData = this.players.get(clipId);
        if (playerData) {
            try {
                if (playerData.isPlaying) {
                    playerData.player.stop();
                    this.activePlayersCount--;
                }
                playerData.player.dispose();
                playerData.gainNode.dispose();
                playerData.panNode.dispose();
            } catch (error) {
                console.warn(`Failed to dispose audio player ${clipId}:`, error);
            }
            this.players.delete(clipId);
            console.log(`Audio player disposed: ${clipId}`);
        }
    }

    disposeAll() {
        console.log('Disposing all audio players');
        this.stopGlobalPlayback();
        this.players.forEach((_, clipId) => {
            this.disposePlayer(clipId);
        });
        this.players.clear();
    }
} 